import { useState, type ReactNode } from 'react';

import { parseVariantKey, type Enchantment } from '@dca/shared';

import { ItemIcon } from './ItemIcon.tsx';

/**
 * Minecraft's own item tooltip.
 *
 * A sword listed as `Diamond Sword#8wJ` tells a buyer nothing. In game they
 * would hover it and read the enchantments, so that is what this does - the
 * same dark panel with the two-tone purple frame, the item name in white, and
 * enchantments in grey below it, levels in Roman numerals.
 *
 * It copies the game rather than inventing a house style because instant
 * recognition is the entire point, which is also why it ignores the dashboard
 * theme: the in-game tooltip is dark in both.
 *
 * An unidentified hash still gets a card. Saying "nobody has identified this
 * one yet" is far more use than silently showing the raw key, and it is where
 * the invitation to name it belongs.
 */
export function ItemTooltip({
  itemKey,
  material,
  title,
  enchantments,
  verified,
  confirmations,
  onLabel,
  children,
}: {
  itemKey: string;
  material: string;
  title: string;
  enchantments: readonly Enchantment[];
  verified: boolean;
  confirmations: number;
  /** Offered when the variant has no label yet. */
  onLabel?: () => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { base, hash } = parseVariantKey(itemKey);
  const identified = enchantments.length > 0;

  return (
    <span
      className="mc-anchor"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
    >
      {children}
      {open && (
        <span className="mc-tooltip" role="tooltip">
          <span className="mc-frame">
            <span className="mc-row">
              {/* Enchanted gear carries the glint it would have in game. */}
              <span className={`mc-slot ${identified ? 'enchanted' : ''}`}>
                <ItemIcon material={material} size={22} />
              </span>
              <span className={`mc-name ${identified ? 'magic' : ''}`}>
                {identified ? base : title}
              </span>
            </span>

            {identified ? (
              <span className="mc-lines">
                {enchantments.map((enchantment) => (
                  <span className="mc-ench" key={`${enchantment.name}-${enchantment.level}`}>
                    {enchantment.level > 0
                      ? `${enchantment.name} ${roman(enchantment.level)}`
                      : enchantment.name}
                  </span>
                ))}
              </span>
            ) : (
              <span className="mc-lines">
                <span className="mc-unknown">Nobody has identified this one yet</span>
              </span>
            )}

            {hash && <span className="mc-meta">Variant #{hash}</span>}

            <span className={`mc-source ${verified ? 'ok' : ''}`}>
              {!identified
                ? 'Hover in game to read it, then tell us'
                : verified
                  ? 'Verified by the shop owner'
                  : confirmations >= 2
                    ? `Confirmed by ${confirmations} contributors`
                    : 'Unconfirmed - one contributor'}
            </span>
          </span>

          {onLabel && !identified && (
            <button
              className="mc-action"
              onMouseDown={(event) => {
                // The tooltip closes on blur, so the click has to be taken on
                // mousedown or the button is gone before it lands.
                event.preventDefault();
                onLabel();
              }}
            >
              I know what this is
            </button>
          )}
        </span>
      )}
    </span>
  );
}

const NUMERALS = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

/** Minecraft writes enchantment levels in Roman numerals, up to X. */
function roman(level: number): string {
  return NUMERALS[level] ?? String(level);
}
